import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";

import API from "../../api/api";
import AdminLayout from "../../components/admin/AdminLayout";

// ============================================================
// CONFIG
// ============================================================

const API_BASE_URL = "https://warfc.com/api";

const STATUS_OPTIONS = [
  {
    value: "upcoming",
    label: "Удахгүй",
    color: "text-blue-400",
    bg: "bg-blue-500/10 border-blue-500/20",
  },
  {
    value: "active",
    label: "Явагдаж байна",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10 border-emerald-500/20",
  },
  {
    value: "completed",
    label: "Дууссан",
    color: "text-neutral-300",
    bg: "bg-neutral-500/10 border-neutral-500/20",
  },
  {
    value: "cancelled",
    label: "Цуцлагдсан",
    color: "text-red-400",
    bg: "bg-red-500/10 border-red-500/20",
  },
  {
    value: "draft",
    label: "Ноорог",
    color: "text-amber-400",
    bg: "bg-amber-500/10 border-amber-500/20",
  },
];

// ============================================================
// HELPERS
// ============================================================

function unwrap(response) {
  if (response?.data !== undefined) {
    return response.data;
  }

  return response;
}

function getTournamentId(tournament) {
  return (
    tournament?.id ??
    tournament?.tournamentid ??
    tournament?.tournament_id ??
    null
  );
}

function getStartDate(tournament) {
  return (
    tournament?.startdate ??
    tournament?.start_date ??
    tournament?.startDate ??
    null
  );
}

function getEndDate(tournament) {
  return (
    tournament?.enddate ??
    tournament?.end_date ??
    tournament?.endDate ??
    null
  );
}

function resolveImage(value) {
  if (!value) return null;

  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }

  if (value.startsWith("/api/")) {
    return `https://warfc.com${value}`;
  }

  if (value.startsWith("/")) {
    return `${API_BASE_URL}${value}`;
  }

  return `${API_BASE_URL}/${value}`;
}

function formatDate(value) {
  if (!value) return "Тодорхойгүй";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Тодорхойгүй";
  }

  return date.toLocaleDateString("mn-MN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatShortDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("mn-MN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function toInputDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    // Handles YYYY-MM-DD directly.
    if (/^\d{4}-\d{2}-\d{2}/.test(String(value))) {
      return String(value).slice(0, 10);
    }

    return "";
  }

  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function getStatusConfig(status) {
  return (
    STATUS_OPTIONS.find(
      (item) => item.value === status
    ) ||
    {
      value: status,
      label: status || "Тодорхойгүй",
      color: "text-neutral-300",
      bg: "bg-neutral-500/10 border-neutral-500/20",
    }
  );
}

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

// ============================================================
// SMALL UI COMPONENTS
// ============================================================

function SectionHeader({
  eyebrow,
  title,
  description,
  action,
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow && (
          <div className="mb-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#d4af37]">
            {eyebrow}
          </div>
        )}

        <h2 className="text-xl font-black tracking-tight text-white sm:text-2xl">
          {title}
        </h2>

        {description && (
          <p className="mt-1 max-w-2xl text-sm leading-6 text-neutral-500">
            {description}
          </p>
        )}
      </div>

      {action}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  detail,
  accent = false,
}) {
  return (
    <div
      className={`
        relative overflow-hidden rounded-2xl border p-4
        ${
          accent
            ? "border-[#d4af37]/30 bg-[#d4af37]/5"
            : "border-neutral-800 bg-neutral-950"
        }
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.16em] text-neutral-500">
            {label}
          </div>

          <div
            className={`
              mt-2 text-3xl font-black tracking-tight
              ${
                accent
                  ? "text-[#d4af37]"
                  : "text-white"
              }
            `}
          >
            {value}
          </div>

          {detail && (
            <div className="mt-1 text-xs text-neutral-600">
              {detail}
            </div>
          )}
        </div>

        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-neutral-800 bg-neutral-900 text-lg">
          {icon}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = getStatusConfig(status);

  return (
    <span
      className={`
        inline-flex items-center gap-2 rounded-full border
        px-3 py-1.5 text-[10px] font-black uppercase tracking-wider
        ${config.bg} ${config.color}
      `}
    >
      <span
        className={`
          h-1.5 w-1.5 rounded-full
          ${
            status === "active"
              ? "animate-pulse bg-emerald-400"
              : "bg-current"
          }
        `}
      />

      {config.label}
    </span>
  );
}

function ProgressBar({
  value,
  label,
  detail,
}) {
  const safeValue = Math.min(
    100,
    Math.max(0, Number(value) || 0)
  );

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-xs font-bold text-neutral-300">
          {label}
        </span>

        <span className="text-xs font-black text-[#d4af37]">
          {detail || `${Math.round(safeValue)}%`}
        </span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-neutral-900">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#8f7220] via-[#d4af37] to-amber-300 transition-all duration-500"
          style={{
            width: `${safeValue}%`,
          }}
        />
      </div>
    </div>
  );
}

// ============================================================
// QUICK ACTION
// ============================================================

function QuickAction({
  to,
  icon,
  title,
  description,
  badge,
}) {
  return (
    <Link
      to={to}
      className="
        group relative overflow-hidden rounded-2xl
        border border-neutral-800 bg-neutral-950 p-5
        transition-all duration-200
        hover:-translate-y-0.5
        hover:border-[#d4af37]/40
        hover:bg-neutral-900
      "
    >
      <div className="flex items-start gap-4">
        <div
          className="
            flex h-12 w-12 shrink-0 items-center
            justify-center rounded-xl
            border border-neutral-800
            bg-neutral-900 text-xl
            transition-colors
            group-hover:border-[#d4af37]/30
          "
        >
          {icon}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-black text-white">
              {title}
            </h3>

            <span className="text-neutral-700 transition-transform group-hover:translate-x-1 group-hover:text-[#d4af37]">
              →
            </span>
          </div>

          <p className="mt-1 text-xs leading-5 text-neutral-500">
            {description}
          </p>

          {badge && (
            <div className="mt-3 inline-flex rounded-full border border-neutral-800 bg-neutral-900 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-neutral-400">
              {badge}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

// ============================================================
// EDIT MODAL
// ============================================================

function EditTournamentModal({
  open,
  tournament,
  saving,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState({
    name: "",
    startdate: "",
    enddate: "",
    location: "",
    status: "upcoming",
  });

  useEffect(() => {
    if (!open || !tournament) return;

    setForm({
      name:
        tournament.name ||
        tournament.title ||
        "",
      startdate: toInputDate(
        getStartDate(tournament)
      ),
      enddate: toInputDate(
        getEndDate(tournament)
      ),
      location:
        tournament.location || "",
      status:
        tournament.status || "upcoming",
    });
  }, [open, tournament]);

  if (!open) return null;

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      alert("Тэмцээний нэр оруулна уу.");
      return;
    }

    if (!form.startdate) {
      alert("Эхлэх огноо сонгоно уу.");
      return;
    }

    if (!form.enddate) {
      alert("Дуусах огноо сонгоно уу.");
      return;
    }

    if (
      form.enddate <
      form.startdate
    ) {
      alert(
        "Дуусах огноо эхлэх огнооноос өмнө байж болохгүй."
      );
      return;
    }

    await onSave({
      name: form.name.trim(),
      startdate: form.startdate,
      enddate: form.enddate,
      location:
        form.location.trim() || null,
      status: form.status,
    });
  };

  return (
    <div
      className="
        fixed inset-0 z-[100]
        flex items-center justify-center
        bg-black/80 p-4 backdrop-blur-sm
      "
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !saving
        ) {
          onClose();
        }
      }}
    >
      <form
        onSubmit={submit}
        className="
          w-full max-w-2xl overflow-hidden
          rounded-3xl border border-neutral-800
          bg-[#0b0b0b] shadow-2xl
        "
      >
        <div className="flex items-start justify-between border-b border-neutral-800 p-5 sm:p-6">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#d4af37]">
              Tournament Settings
            </div>

            <h2 className="mt-1 text-xl font-black text-white">
              Тэмцээний мэдээлэл засах
            </h2>

            <p className="mt-1 text-xs text-neutral-500">
              Үндсэн мэдээллийг шинэчилнэ.
            </p>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="
              flex h-9 w-9 items-center justify-center
              rounded-xl border border-neutral-800
              bg-neutral-900 text-neutral-400
              transition hover:border-neutral-700
              hover:text-white disabled:opacity-40
            "
          >
            ×
          </button>
        </div>

        <div className="space-y-5 p-5 sm:p-6">
          {/* NAME */}
          <div>
            <label className="mb-2 block text-[10px] font-black uppercase tracking-wider text-neutral-500">
              Тэмцээний нэр *
            </label>

            <input
              value={form.name}
              onChange={(e) =>
                updateField(
                  "name",
                  e.target.value
                )
              }
              className="
                w-full rounded-xl border border-neutral-800
                bg-neutral-950 px-4 py-3
                text-sm font-bold text-white
                outline-none transition
                placeholder:text-neutral-700
                focus:border-[#d4af37]/60
              "
              placeholder="WAR FC National Championship"
            />
          </div>

          {/* DATES */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-[10px] font-black uppercase tracking-wider text-neutral-500">
                Эхлэх огноо *
              </label>

              <input
                type="date"
                value={form.startdate}
                onChange={(e) =>
                  updateField(
                    "startdate",
                    e.target.value
                  )
                }
                className="
                  w-full rounded-xl border border-neutral-800
                  bg-neutral-950 px-4 py-3
                  text-sm font-bold text-white
                  outline-none focus:border-[#d4af37]/60
                "
              />
            </div>

            <div>
              <label className="mb-2 block text-[10px] font-black uppercase tracking-wider text-neutral-500">
                Дуусах огноо *
              </label>

              <input
                type="date"
                value={form.enddate}
                min={form.startdate || undefined}
                onChange={(e) =>
                  updateField(
                    "enddate",
                    e.target.value
                  )
                }
                className="
                  w-full rounded-xl border border-neutral-800
                  bg-neutral-950 px-4 py-3
                  text-sm font-bold text-white
                  outline-none focus:border-[#d4af37]/60
                "
              />
            </div>
          </div>

          {/* LOCATION */}
          <div>
            <label className="mb-2 block text-[10px] font-black uppercase tracking-wider text-neutral-500">
              Байршил / Venue
            </label>

            <input
              value={form.location}
              onChange={(e) =>
                updateField(
                  "location",
                  e.target.value
                )
              }
              className="
                w-full rounded-xl border border-neutral-800
                bg-neutral-950 px-4 py-3
                text-sm font-bold text-white
                outline-none placeholder:text-neutral-700
                focus:border-[#d4af37]/60
              "
              placeholder="Ulaanbaatar, Mongolia"
            />
          </div>

          {/* STATUS */}
          <div>
            <label className="mb-2 block text-[10px] font-black uppercase tracking-wider text-neutral-500">
              Төлөв
            </label>

            <select
              value={form.status}
              onChange={(e) =>
                updateField(
                  "status",
                  e.target.value
                )
              }
              className="
                w-full appearance-none rounded-xl
                border border-neutral-800
                bg-neutral-950 px-4 py-3
                text-sm font-bold text-white
                outline-none focus:border-[#d4af37]/60
              "
            >
              {STATUS_OPTIONS.map(
                (option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
            <div className="text-xs font-black text-amber-400">
              ⚠️ Анхаарах зүйл
            </div>

            <p className="mt-1 text-xs leading-5 text-neutral-500">
              Энд зөвхөн тэмцээний үндсэн мэдээлэл
              өөрчлөгдөнө. Bracket, match болон
              бүртгэлүүд автоматаар reset болохгүй.
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-neutral-800 p-5 sm:flex-row sm:justify-end sm:p-6">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="
              rounded-xl border border-neutral-800
              bg-neutral-900 px-5 py-3
              text-xs font-black uppercase
              tracking-wider text-neutral-300
              hover:border-neutral-700
              disabled:opacity-40
            "
          >
            Болих
          </button>

          <button
            type="submit"
            disabled={saving}
            className="
              rounded-xl border border-[#d4af37]
              bg-gradient-to-r from-[#d4af37] to-amber-500
              px-5 py-3 text-xs font-black uppercase
              tracking-wider text-black
              shadow-lg shadow-[#d4af37]/10
              hover:brightness-110
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            {saving
              ? "Хадгалж байна..."
              : "✓ Өөрчлөлт хадгалах"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================

export default function TournamentManage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const tournamentId = Number(id);

  const [tournament, setTournament] =
    useState(null);

  const [registrations, setRegistrations] =
    useState([]);

  const [brackets, setBrackets] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [statsLoading, setStatsLoading] =
    useState(true);

  const [uploading, setUploading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [editOpen, setEditOpen] =
    useState(false);

  const [imagePreview, setImagePreview] =
    useState(null);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  // ==========================================================
  // LOAD TOURNAMENT
  // ==========================================================

  const loadTournament = useCallback(
    async () => {
      if (
        !Number.isInteger(
          tournamentId
        ) ||
        tournamentId <= 0
      ) {
        setError(
          "Тэмцээний ID буруу байна."
        );
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response =
          await API.get(
            `/tournaments/${tournamentId}`
          );

        const data = unwrap(response);

        const tournamentData =
          data?.tournament ||
          data ||
          null;

        setTournament(
          tournamentData
        );

        const image =
          tournamentData?.image_url ||
          tournamentData?.image ||
          null;

        setImagePreview(
          resolveImage(image)
        );
      } catch (err) {
        console.error(
          "Failed to load tournament:",
          err
        );

        setError(
          getErrorMessage(
            err,
            "Тэмцээний мэдээллийг ачааллахад алдаа гарлаа."
          )
        );
      } finally {
        setLoading(false);
      }
    },
    [tournamentId]
  );

  // ==========================================================
  // LOAD REGISTRATIONS
  // ==========================================================

  const loadRegistrations =
    useCallback(async () => {
      if (
        !Number.isInteger(
          tournamentId
        ) ||
        tournamentId <= 0
      ) {
        return;
      }

      try {
        setStatsLoading(true);

        const response =
          await API.get(
            `/tournaments/${tournamentId}/registration`
          );

        const data = unwrap(response);

        const list = Array.isArray(
          data
        )
          ? data
          : Array.isArray(
              data?.registrations
            )
          ? data.registrations
          : [];

        setRegistrations(list);
      } catch (err) {
        console.error(
          "Failed to load registrations:",
          err
        );

        // Statistics should never
        // break the whole management page.
        setRegistrations([]);
      } finally {
        setStatsLoading(false);
      }
    }, [tournamentId]);

  // ==========================================================
  // LOAD BRACKETS
  // ==========================================================

  const loadBrackets =
    useCallback(async () => {
      if (
        !Number.isInteger(
          tournamentId
        ) ||
        tournamentId <= 0
      ) {
        return;
      }

      try {
        const response =
          await API.get(
            `/tournaments/${tournamentId}/brackets`
          );

        const data = unwrap(response);

        const list = Array.isArray(
          data
        )
          ? data
          : Array.isArray(
              data?.brackets
            )
          ? data.brackets
          : [];

        setBrackets(list);
      } catch (err) {
        console.warn(
          "Could not load tournament brackets:",
          err
        );

        setBrackets([]);
      }
    }, [tournamentId]);

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    loadTournament();
    loadRegistrations();
    loadBrackets();
  }, [
    loadTournament,
    loadRegistrations,
    loadBrackets,
  ]);

  // ==========================================================
  // UPLOAD POSTER
  // ==========================================================

  const handleUpload = async (
    event
  ) => {
    const file =
      event.target.files?.[0];

    if (!file) return;

    // Client-side safety.
    if (
      !file.type.startsWith(
        "image/"
      )
    ) {
      alert(
        "Зөвхөн image файл сонгоно уу."
      );

      event.target.value = "";
      return;
    }

    if (
      file.size >
      10 * 1024 * 1024
    ) {
      alert(
        "Зураг 10MB-аас бага хэмжээтэй байх ёстой."
      );

      event.target.value = "";
      return;
    }

    try {
      setUploading(true);
      setSuccess("");
      setError("");

      const localPreview =
        URL.createObjectURL(file);

      setImagePreview(
        localPreview
      );

      const formData =
        new FormData();

      formData.append(
        "image",
        file
      );

      const response =
        await API.postForm(
          `/tournaments/${tournamentId}/upload-image`,
          formData
        );

      const data =
        unwrap(response);

      const imageUrl =
        data?.imageUrl ||
        data?.image_url ||
        null;

      if (imageUrl) {
        setImagePreview(
          resolveImage(imageUrl)
        );

        setTournament(
          (prev) =>
            prev
              ? {
                  ...prev,
                  image_url:
                    imageUrl,
                }
              : prev
        );
      }

      setSuccess(
        "Тэмцээний постер амжилттай шинэчлэгдлээ."
      );
    } catch (err) {
      console.error(
        "Tournament image upload error:",
        err
      );

      setError(
        getErrorMessage(
          err,
          "Постер upload хийхэд алдаа гарлаа."
        )
      );

      await loadTournament();
    } finally {
      setUploading(false);

      event.target.value = "";
    }
  };

  // ==========================================================
  // SAVE TOURNAMENT
  // ==========================================================

  const handleSaveTournament =
    async (payload) => {
      try {
        setSaving(true);
        setError("");
        setSuccess("");

        const response =
          await API.patch(
            `/tournaments/${tournamentId}`,
            payload
          );

        const data =
          unwrap(response);

        const updated =
          data?.tournament ||
          data ||
          null;

        if (updated) {
          setTournament(
            updated
          );
        } else {
          await loadTournament();
        }

        setEditOpen(false);

        setSuccess(
          "Тэмцээний мэдээлэл амжилттай шинэчлэгдлээ."
        );
      } catch (err) {
        console.error(
          "Update tournament error:",
          err
        );

        setError(
          getErrorMessage(
            err,
            "Тэмцээний мэдээлэл шинэчлэхэд алдаа гарлаа."
          )
        );
      } finally {
        setSaving(false);
      }
    };

  // ==========================================================
  // STATISTICS
  // ==========================================================

  const stats = useMemo(() => {
    const total =
      registrations.length;

    const active =
      registrations.filter(
        (r) =>
          !r.status ||
          r.status === "active"
      ).length;

    const paid =
      registrations.filter(
        (r) =>
          Boolean(r.paid)
      ).length;

    const approved =
      registrations.filter(
        (r) =>
          Boolean(r.approved)
      ).length;

    const weighed =
      registrations.filter(
        (r) =>
          Boolean(r.weighed_in)
      ).length;

    const pending =
      registrations.filter(
        (r) =>
          !r.approved &&
          (!r.status ||
            r.status ===
              "active")
      ).length;

    const cancelled =
      registrations.filter(
        (r) =>
          r.status ===
          "cancelled"
      ).length;

    const clubs = new Set(
      registrations
        .map(
          (r) =>
            r.club_id ??
            r.club_name ??
            r.club
        )
        .filter(Boolean)
    ).size;

    return {
      total,
      active,
      paid,
      approved,
      weighed,
      pending,
      cancelled,
      clubs,
    };
  }, [registrations]);

  // ==========================================================
  // PROGRESS
  // ==========================================================

  const progress = useMemo(() => {
    const total =
      stats.active;

    const registration =
      total > 0
        ? 100
        : 0;

    const approval =
      total > 0
        ? (stats.approved /
            total) *
          100
        : 0;

    const payment =
      total > 0
        ? (stats.paid /
            total) *
          100
        : 0;

    const weighIn =
      total > 0
        ? (stats.weighed /
            total) *
          100
        : 0;

    return {
      registration,
      approval,
      payment,
      weighIn,
    };
  }, [stats]);

  // ==========================================================
  // DATA
  // ==========================================================

  const name =
    tournament?.name ||
    tournament?.title ||
    "Тэмцээн";

  const startDate =
    getStartDate(
      tournament
    );

  const endDate =
    getEndDate(
      tournament
    );

  const location =
    tournament?.location ||
    "Байршил тодорхойгүй";

  const status =
    tournament?.status ||
    "upcoming";

  const tournamentImage =
    imagePreview ||
    resolveImage(
      tournament?.image_url
    );

  // ==========================================================
  // DATE DISPLAY
  // ==========================================================

  const dateRange = useMemo(() => {
    if (!startDate) {
      return "Огноо тодорхойгүй";
    }

    if (!endDate) {
      return formatDate(
        startDate
      );
    }

    const start =
      new Date(startDate);

    const end =
      new Date(endDate);

    if (
      Number.isNaN(
        start.getTime()
      ) ||
      Number.isNaN(
        end.getTime()
      )
    ) {
      return "Огноо тодорхойгүй";
    }

    if (
      start.toDateString() ===
      end.toDateString()
    ) {
      return formatDate(
        startDate
      );
    }

    return `${formatDate(
      startDate
    )} – ${formatDate(
      endDate
    )}`;
  }, [
    startDate,
    endDate,
  ]);

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex min-h-[70vh] items-center justify-center p-6">
          <div className="text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-neutral-800 border-t-[#d4af37]" />

            <p className="mt-4 text-xs font-black uppercase tracking-wider text-neutral-500">
              Tournament Control Center
              ачааллаж байна...
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // ==========================================================
  // ERROR
  // ==========================================================

  if (!tournament) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="mx-auto max-w-3xl rounded-3xl border border-red-500/20 bg-red-500/5 p-8 text-center">
            <div className="text-4xl">
              ⚠️
            </div>

            <h1 className="mt-4 text-xl font-black text-white">
              Тэмцээн олдсонгүй
            </h1>

            <p className="mt-2 text-sm text-neutral-500">
              {error ||
                "Тэмцээний мэдээллийг ачааллах боломжгүй байна."}
            </p>

            <button
              onClick={() =>
                navigate(
                  "/admin/tournaments"
                )
              }
              className="
                mt-6 rounded-xl
                border border-neutral-800
                bg-neutral-900
                px-5 py-3
                text-xs font-black
                uppercase tracking-wider
                text-neutral-300
              "
            >
              ← Тэмцээнүүд рүү буцах
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#050505]">
        <div className="mx-auto max-w-[1500px] p-4 sm:p-6 lg:p-8">
          {/* ==================================================
              TOP NAV
          ================================================== */}

          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() =>
                  navigate(-1)
                }
                className="
                  flex h-10 w-10 items-center
                  justify-center rounded-xl
                  border border-neutral-800
                  bg-neutral-950
                  text-neutral-400
                  transition hover:border-neutral-700
                  hover:text-white
                "
              >
                ←
              </button>

              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.22em] text-[#d4af37]">
                  WAR FC ADMIN
                </div>

                <div className="text-sm font-black text-neutral-400">
                  Tournament Control Center
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Link
                to={`/tournaments/${tournamentId}`}
                className="
                  rounded-xl border border-neutral-800
                  bg-neutral-950 px-4 py-2.5
                  text-[10px] font-black uppercase
                  tracking-wider text-neutral-400
                  transition hover:border-[#d4af37]/40
                  hover:text-[#d4af37]
                "
              >
                👁 Public View
              </Link>

              <button
                onClick={() =>
                  setEditOpen(true)
                }
                className="
                  rounded-xl border border-[#d4af37]
                  bg-gradient-to-r
                  from-[#d4af37] to-amber-500
                  px-4 py-2.5
                  text-[10px] font-black uppercase
                  tracking-wider text-black
                  shadow-lg shadow-[#d4af37]/10
                  hover:brightness-110
                "
              >
                ✎ Тэмцээн засах
              </button>
            </div>
          </div>

          {/* ==================================================
              ALERTS
          ================================================== */}

          {error && (
            <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-500/20 bg-red-500/5 p-4">
              <span>⚠️</span>

              <div className="flex-1">
                <div className="text-xs font-black text-red-400">
                  Алдаа
                </div>

                <div className="mt-1 text-xs leading-5 text-neutral-500">
                  {error}
                </div>
              </div>

              <button
                onClick={() =>
                  setError("")
                }
                className="text-neutral-600 hover:text-white"
              >
                ×
              </button>
            </div>
          )}

          {success && (
            <div className="mb-5 flex items-start gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
              <span>✓</span>

              <div className="flex-1">
                <div className="text-xs font-black text-emerald-400">
                  Амжилттай
                </div>

                <div className="mt-1 text-xs text-neutral-500">
                  {success}
                </div>
              </div>

              <button
                onClick={() =>
                  setSuccess("")
                }
                className="text-neutral-600 hover:text-white"
              >
                ×
              </button>
            </div>
          )}

          {/* ==================================================
              HERO
          ================================================== */}

          <section
            className="
              relative overflow-hidden
              rounded-3xl border border-neutral-800
              bg-neutral-950 shadow-2xl
            "
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />

            {tournamentImage && (
              <img
                src={tournamentImage}
                alt={name}
                className="
                  absolute inset-0 h-full w-full
                  object-cover opacity-30
                "
              />
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30" />

            <div className="relative z-10 grid gap-8 p-5 sm:p-8 lg:grid-cols-[1fr_300px] lg:p-10">
              <div className="flex min-w-0 flex-col justify-end">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <StatusBadge status={status} />

                  <span className="rounded-full border border-neutral-800 bg-black/50 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-neutral-500">
                    ID #{getTournamentId(
                      tournament
                    )}
                  </span>
                </div>

                <h1 className="max-w-4xl text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                  {name}
                </h1>

                <div className="mt-5 grid gap-3 text-sm text-neutral-400 sm:grid-cols-2 lg:max-w-3xl">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">
                      📅
                    </span>

                    <div>
                      <div className="text-[9px] font-black uppercase tracking-wider text-neutral-600">
                        Огноо
                      </div>

                      <div className="font-bold text-neutral-200">
                        {dateRange}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-lg">
                      📍
                    </span>

                    <div>
                      <div className="text-[9px] font-black uppercase tracking-wider text-neutral-600">
                        Байршил
                      </div>

                      <div className="font-bold text-neutral-200">
                        {location}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* POSTER */}
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-neutral-800 bg-black">
                  {tournamentImage ? (
                    <img
                      src={tournamentImage}
                      alt={`${name} poster`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center text-center">
                      <div className="text-5xl opacity-20">
                        🥊
                      </div>

                      <div className="mt-3 text-[10px] font-black uppercase tracking-wider text-neutral-700">
                        Poster байхгүй
                      </div>
                    </div>
                  )}

                  {uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                      <div className="text-center">
                        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-neutral-700 border-t-[#d4af37]" />

                        <div className="mt-3 text-[9px] font-black uppercase tracking-wider text-neutral-400">
                          Uploading...
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <label
                  className="
                    mt-3 flex cursor-pointer
                    items-center justify-center gap-2
                    rounded-xl border border-neutral-800
                    bg-black/70 px-4 py-3
                    text-[10px] font-black uppercase
                    tracking-wider text-neutral-300
                    transition hover:border-[#d4af37]/50
                    hover:text-[#d4af37]
                  "
                >
                  📷 Постер солих

                  <input
                    type="file"
                    accept="image/*"
                    onChange={
                      handleUpload
                    }
                    disabled={
                      uploading
                    }
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </section>

          {/* ==================================================
              KPI
          ================================================== */}

          <section className="mt-6">
            <SectionHeader
              eyebrow="Tournament Overview"
              title="Тэмцээний үзүүлэлт"
              description="Бүртгэл болон тэмцээний бэлтгэлийн одоогийн төлөв."
            />

            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard
                icon="🥊"
                label="Нийт бүртгэл"
                value={
                  statsLoading
                    ? "—"
                    : stats.total
                }
                detail={`${stats.active} идэвхтэй`}
                accent
              />

              <StatCard
                icon="✓"
                label="Баталгаажсан"
                value={
                  statsLoading
                    ? "—"
                    : stats.approved
                }
                detail={`${Math.round(
                  progress.approval
                )}%`}
              />

              <StatCard
                icon="₮"
                label="Төлбөр"
                value={
                  statsLoading
                    ? "—"
                    : stats.paid
                }
                detail={`${Math.round(
                  progress.payment
                )}% paid`}
              />

              <StatCard
                icon="⚖️"
                label="Жин үзсэн"
                value={
                  statsLoading
                    ? "—"
                    : stats.weighed
                }
                detail={`${Math.round(
                  progress.weighIn
                )}%`}
              />
            </div>
          </section>

          {/* ==================================================
              STATUS / PROGRESS
          ================================================== */}

          <section className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
            <div className="rounded-3xl border border-neutral-800 bg-neutral-950 p-5 sm:p-6">
              <SectionHeader
                eyebrow="Preparation"
                title="Тэмцээний бэлтгэлийн явц"
                description="Тэмцээн эхлэхээс өмнө admin талаас хянах гол үзүүлэлтүүд."
              />

              <div className="mt-6 space-y-6">
                <ProgressBar
                  label="Бүртгэл"
                  value={
                    progress.registration
                  }
                  detail={`${stats.active} active`}
                />

                <ProgressBar
                  label="Баталгаажуулалт"
                  value={
                    progress.approval
                  }
                  detail={`${stats.approved} / ${stats.active}`}
                />

                <ProgressBar
                  label="Төлбөр"
                  value={
                    progress.payment
                  }
                  detail={`${stats.paid} / ${stats.active}`}
                />

                <ProgressBar
                  label="Weigh-in"
                  value={
                    progress.weighIn
                  }
                  detail={`${stats.weighed} / ${stats.active}`}
                />
              </div>
            </div>

            <div className="rounded-3xl border border-neutral-800 bg-neutral-950 p-5 sm:p-6">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#d4af37]">
                Quick Status
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between rounded-xl border border-neutral-800 bg-neutral-900/50 p-3">
                  <span className="text-xs text-neutral-500">
                    Хүлээгдэж буй
                  </span>

                  <span className="font-black text-amber-400">
                    {stats.pending}
                  </span>
                </div>

                <div className="flex items-center justify-between rounded-xl border border-neutral-800 bg-neutral-900/50 p-3">
                  <span className="text-xs text-neutral-500">
                    Клуб
                  </span>

                  <span className="font-black text-white">
                    {stats.clubs}
                  </span>
                </div>

                <div className="flex items-center justify-between rounded-xl border border-neutral-800 bg-neutral-900/50 p-3">
                  <span className="text-xs text-neutral-500">
                    Bracket
                  </span>

                  <span className="font-black text-white">
                    {brackets.length}
                  </span>
                </div>

                <div className="flex items-center justify-between rounded-xl border border-neutral-800 bg-neutral-900/50 p-3">
                  <span className="text-xs text-neutral-500">
                    Цуцлагдсан
                  </span>

                  <span className="font-black text-red-400">
                    {stats.cancelled}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* ==================================================
              MANAGEMENT
          ================================================== */}

          <section className="mt-10">
            <SectionHeader
              eyebrow="Management"
              title="Тэмцээний удирдлага"
              description="Тэмцээний бүх үндсэн ажиллагааг нэг төвөөс удирдана."
            />

            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <QuickAction
                to={`/admin/tournaments/${tournamentId}/fighters`}
                icon="🥊"
                title="Тамирчид"
                description="Бүртгэлтэй тамирчдыг харах, шалгах, approval болон төлбөрийн төлөв удирдах."
                badge={`${stats.total} registrations`}
              />

              <QuickAction
                to={`/admin/tournaments/${tournamentId}/my-fighters`}
                icon="👥"
                title="Клубын бүртгэл"
                description="Клубуудын тэмцээнд бүртгүүлсэн тамирчдыг удирдах."
              />

              <QuickAction
                to={`/admin/tournaments/${tournamentId}/create-bracket`}
                icon="⚔️"
                title="Тэмцээний хослол"
                description="Bracket үүсгэх, засах, fighter placement болон matchup удирдах."
                badge={`${brackets.length} brackets`}
              />

              <QuickAction
                to={`/admin/tournament/${tournamentId}/fight-schedule`}
                icon="🗓️"
                title="Fight Schedule"
                description="Тулааны дараалал, ring, fight number болон цагийн хуваарь."
              />

              <QuickAction
                to={`/admin/tournament/${tournamentId}/rules`}
                icon="📋"
                title="Тэмцээний дүрэм"
                description="Дүрмийн хэсгүүд, scoring, rounds болон тэмцээний журам."
              />

              <QuickAction
                to={`/tournaments/${tournamentId}/brackets`}
                icon="📊"
                title="Public Bracket"
                description="Оролцогчдод харагдах тэмцээний хослолын public хувилбар."
              />
            </div>
          </section>

          {/* ==================================================
              WORKFLOW
          ================================================== */}

          <section className="mt-10">
            <SectionHeader
              eyebrow="Event Workflow"
              title="Тэмцээний workflow"
              description="WAR FC тэмцээнийг удирдах үндсэн дараалал."
            />

            <div className="mt-4 overflow-hidden rounded-3xl border border-neutral-800 bg-neutral-950">
              <div className="grid md:grid-cols-4">
                {[
                  {
                    number: "01",
                    icon: "👥",
                    title: "Registration",
                    description:
                      "Тамирчид бүртгүүлнэ",
                    active:
                      stats.total > 0,
                  },
                  {
                    number: "02",
                    icon: "⚖️",
                    title: "Weigh-in",
                    description:
                      "Жин шалгана",
                    active:
                      stats.weighed > 0,
                  },
                  {
                    number: "03",
                    icon: "⚔️",
                    title: "Brackets",
                    description:
                      "Хослол үүсгэнэ",
                    active:
                      brackets.length > 0,
                  },
                  {
                    number: "04",
                    icon: "🥊",
                    title: "Live Matches",
                    description:
                      "Тулаан явагдана",
                    active:
                      status ===
                      "active",
                  },
                ].map(
                  (
                    step,
                    index
                  ) => (
                    <div
                      key={
                        step.number
                      }
                      className={`
                        relative p-5
                        ${
                          index <
                          3
                            ? "border-b md:border-b-0 md:border-r border-neutral-800"
                            : ""
                        }
                      `}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`
                            flex h-11 w-11
                            shrink-0 items-center
                            justify-center rounded-xl
                            border text-lg
                            ${
                              step.active
                                ? "border-[#d4af37]/30 bg-[#d4af37]/10"
                                : "border-neutral-800 bg-neutral-900"
                            }
                          `}
                        >
                          {
                            step.icon
                          }
                        </div>

                        <div>
                          <div className="text-[9px] font-black tracking-[0.2em] text-neutral-700">
                            {
                              step.number
                            }
                          </div>

                          <div className="mt-1 font-black text-white">
                            {
                              step.title
                            }
                          </div>

                          <div className="mt-1 text-xs text-neutral-600">
                            {
                              step.description
                            }
                          </div>

                          <div
                            className={`
                              mt-3 text-[9px]
                              font-black uppercase
                              tracking-wider
                              ${
                                step.active
                                  ? "text-emerald-400"
                                  : "text-neutral-700"
                              }
                            `}
                          >
                            {step.active
                              ? "● Ready"
                              : "○ Pending"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </section>

          {/* ==================================================
              TOURNAMENT INFO
          ================================================== */}

          <section className="mt-10">
            <SectionHeader
              eyebrow="Tournament Information"
              title="Үндсэн мэдээлэл"
              action={
                <button
                  onClick={() =>
                    setEditOpen(true)
                  }
                  className="
                    rounded-xl border border-neutral-800
                    bg-neutral-950 px-4 py-2.5
                    text-[10px] font-black uppercase
                    tracking-wider text-neutral-300
                    hover:border-[#d4af37]/40
                    hover:text-[#d4af37]
                  "
                >
                  ✎ Засах
                </button>
              }
            />

            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4">
                <div className="text-[9px] font-black uppercase tracking-wider text-neutral-600">
                  Tournament ID
                </div>

                <div className="mt-2 font-black text-white">
                  #{getTournamentId(
                    tournament
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4">
                <div className="text-[9px] font-black uppercase tracking-wider text-neutral-600">
                  Start Date
                </div>

                <div className="mt-2 font-black text-white">
                  {formatDate(
                    startDate
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4">
                <div className="text-[9px] font-black uppercase tracking-wider text-neutral-600">
                  End Date
                </div>

                <div className="mt-2 font-black text-white">
                  {endDate
                    ? formatDate(
                        endDate
                      )
                    : "Тодорхойгүй"}
                </div>
              </div>

              <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4">
                <div className="text-[9px] font-black uppercase tracking-wider text-neutral-600">
                  Location
                </div>

                <div className="mt-2 truncate font-black text-white">
                  {location}
                </div>
              </div>
            </div>
          </section>

          {/* ==================================================
              DANGER ZONE
          ================================================== */}

          <section className="mt-10 pb-10">
            <div className="rounded-3xl border border-red-500/20 bg-red-500/[0.025] p-5 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-red-400">
                    Danger Zone
                  </div>

                  <h2 className="mt-1 text-lg font-black text-white">
                    Аюултай үйлдлүүд
                  </h2>

                  <p className="mt-1 max-w-2xl text-xs leading-5 text-neutral-600">
                    Bracket reset, tournament deletion
                    зэрэг irreversible үйлдлийг тусдаа
                    хуудсаар баталгаажуулж хийх нь зөв.
                  </p>
                </div>

                <Link
                  to={`/admin/tournaments/${tournamentId}/create-bracket`}
                  className="
                    rounded-xl border border-red-500/20
                    bg-red-500/5 px-4 py-2.5
                    text-center text-[10px]
                    font-black uppercase
                    tracking-wider text-red-400
                    hover:bg-red-500/10
                  "
                >
                  Bracket Control →
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ======================================================
          EDIT MODAL
      ====================================================== */}

      <EditTournamentModal
        open={editOpen}
        tournament={tournament}
        saving={saving}
        onClose={() =>
          !saving &&
          setEditOpen(false)
        }
        onSave={
          handleSaveTournament
        }
      />
    </AdminLayout>
  );
}